/*
 * INVENTORY DASHBOARD — v1.10.8 (2026-05-09)
 *
 * Three sections:
 *   A — Fleet totals: per-part total qty New + Broken + warehouse share %
 *   B — Holdings by holder: per-holder breakdown (sortable)
 *   C — Part consumption: parts_delta with qty_returned_failed > 0 in window;
 *       trend vs prior window; top 3 sites / robots / techs per part
 *
 * Data source: SQLite (parts_delta + dispatching_events) on the desktop.
 * Mobile EOD review writes parts_delta TODAY; future desktop ticket-close
 * MUST also write to the same parts_delta schema (single ledger). DE flow
 * does NOT count as consumption — broken parts that change hands via DE
 * already existed; consumption is the moment qty_returned_failed > 0.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getInventoryFleetTotals, getInventoryHoldings, getInventoryConsumption,
} from '../services/api';

const WINDOW_OPTIONS = [30, 60, 90, 120, 150, 180];

export default function Inventory() {
  return (
    <div style={pStyles.page}>
      <h1 style={pStyles.title}>Inventory Dashboard</h1>
      <p style={pStyles.subtitle}>
        Fleet totals · holdings by holder · part consumption.
        <span style={{color: '#b2bec3', marginLeft: 8}}>
          v1.10.8 — single parts_delta ledger across mobile EOD + desktop ticket-close.
        </span>
      </p>

      <FleetTotals />
      <Holdings />
      <Consumption />
    </div>
  );
}

// ─── Section A — Fleet totals ──────────────────────────────────────────

function FleetTotals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    getInventoryFleetTotals()
      .then(r => { if (!cancelled) setRows(r.rows || []); })
      .catch(e => { if (!cancelled) setError(e.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => (r.part_id || '').toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <Section title="A — Fleet totals" subtitle={`${rows.length} parts in fleet`}
              right={<input style={pStyles.search} placeholder="filter by part…"
                              value={search} onChange={e => setSearch(e.target.value)} />}>
      {loading && <Loading />}
      {error && <ErrorBox message={error} />}
      {!loading && rows.length === 0 && <Empty msg="No inventory recorded yet — confirm a DE to populate holdings." />}
      {!loading && rows.length > 0 && (
        <table style={pStyles.table}>
          <thead><tr>
            <th style={pStyles.th}>Part</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Qty New</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Qty Broken</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Total</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Warehouse share</th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.part_id} style={pStyles.tr}>
                <td style={pStyles.td}><strong>{r.part_id}</strong></td>
                <td style={{...pStyles.td, textAlign: 'right'}}>{r.total_new}</td>
                <td style={{...pStyles.td, textAlign: 'right',
                              color: r.total_broken > 0 ? '#c0392b' : 'inherit'}}>
                  {r.total_broken}</td>
                <td style={{...pStyles.td, textAlign: 'right', fontWeight: 600}}>
                  {r.total_new + r.total_broken}</td>
                <td style={{...pStyles.td, textAlign: 'right'}}>
                  <ShareBar pct={r.warehouse_share_pct} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function ShareBar({ pct }) {
  const safePct = Math.max(0, Math.min(100, pct || 0));
  return (
    <div style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
      <div style={{width: 80, height: 6, background: '#e4e8ee', borderRadius: 3, overflow: 'hidden'}}>
        <div style={{width: `${safePct}%`, height: '100%', background: '#475569'}} />
      </div>
      <span style={{fontSize: 12, color: '#636e72', minWidth: 40, textAlign: 'right'}}>
        {safePct.toFixed(0)}%
      </span>
    </div>
  );
}

// ─── Section B — Holdings by holder ────────────────────────────────────

function Holdings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    getInventoryHoldings()
      .then(r => { if (!cancelled) setRows(r.rows || []); })
      .catch(e => { if (!cancelled) setError(e.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => (r.holder_name || '').toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <Section title="B — Holdings by holder" subtitle={`${rows.length} holders`}
              right={<input style={pStyles.search} placeholder="search by name…"
                              value={search} onChange={e => setSearch(e.target.value)} />}>
      {loading && <Loading />}
      {error && <ErrorBox message={error} />}
      {!loading && rows.length === 0 && <Empty msg="No holdings yet — confirm a DE to start tracking inventory." />}
      {!loading && rows.length > 0 && (
        <table style={pStyles.table}>
          <thead><tr>
            <th style={pStyles.th}>Holder</th>
            <th style={pStyles.th}>Role</th>
            <th style={pStyles.th}>Parts held</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Total units</th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.holder_id} style={pStyles.tr}>
                <td style={pStyles.td}><strong>{r.holder_name}</strong></td>
                <td style={pStyles.td}>
                  <span style={{...pStyles.roleBadge,
                                 textTransform: 'capitalize'}}>
                    {(r.role || 'unknown').replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={pStyles.td}>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                    {(r.parts || []).map(p => (
                      <span key={p.part_id} style={pStyles.partChip}
                             title={`${p.qty_new} new · ${p.qty_broken} broken`}>
                        {p.part_id} <small style={{color: '#636e72'}}>
                          {p.qty_new}/<span style={{color: p.qty_broken > 0 ? '#c0392b' : '#636e72'}}>{p.qty_broken}</span>
                        </small>
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{...pStyles.td, textAlign: 'right', fontWeight: 600}}>
                  {r.total_units}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// ─── Section C — Part consumption ──────────────────────────────────────

function Consumption() {
  const [days, setDays] = useState(30);
  const [siteFilter, setSiteFilter] = useState('');
  const [robotFilter, setRobotFilter] = useState('');
  const [techFilter, setTechFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({});

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const opts = { days };
      if (siteFilter) opts.site = siteFilter;
      if (robotFilter) opts.robot = parseInt(robotFilter, 10);
      if (techFilter) opts.techId = techFilter;
      const r = await getInventoryConsumption(opts);
      setRows(r.rows || []);
      setMeta({ window_days: r.window_days, since: r.since });
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [days, siteFilter, robotFilter, techFilter]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Section
      title="C — Part consumption"
      subtitle={meta.since ? `${rows.length} parts consumed · since ${meta.since.slice(0, 10)}` : ''}
      right={
        <div style={{display: 'flex', gap: 6}}>
          {WINDOW_OPTIONS.map(d => (
            <button key={d}
                    onClick={() => setDays(d)}
                    style={{
                      padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      border: '1px solid ' + (days === d ? '#475569' : '#cbd5e1'),
                      background: days === d ? '#475569' : '#fff',
                      color: days === d ? '#fff' : '#475569',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>{d}d</button>
          ))}
        </div>
      }>
      <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap'}}>
        <input style={pStyles.search} placeholder="filter by site (project_id)…"
               value={siteFilter} onChange={e => setSiteFilter(e.target.value)} />
        <input style={pStyles.search} placeholder="robot # (e.g. 79)…"
               value={robotFilter} onChange={e => setRobotFilter(e.target.value)} />
        <input style={pStyles.search} placeholder="tech (id)…"
               value={techFilter} onChange={e => setTechFilter(e.target.value)} />
      </div>
      {loading && <Loading />}
      {error && <ErrorBox message={error} />}
      {!loading && rows.length === 0 && (
        <Empty msg={`No parts consumed in last ${days} days. Mobile EOD reviews + desktop ticket-closes write to parts_delta — once a tech submits feedback with parts replaced, rows will appear here.`} />
      )}
      {!loading && rows.length > 0 && (
        <table style={pStyles.table}>
          <thead><tr>
            <th style={pStyles.th}>Part</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Consumed</th>
            <th style={{...pStyles.th, textAlign: 'right'}}>Trend</th>
            <th style={pStyles.th}>Top sites</th>
            <th style={pStyles.th}>Top robots</th>
            <th style={pStyles.th}>Top techs</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.part_id} style={pStyles.tr}>
                <td style={pStyles.td}><strong>{r.part_id}</strong></td>
                <td style={{...pStyles.td, textAlign: 'right', fontWeight: 600,
                              color: '#c0392b'}}>{r.qty_consumed}</td>
                <td style={{...pStyles.td, textAlign: 'right'}}>
                  <TrendCell trend={r.trend_pct} prior={r.prior_qty} />
                </td>
                <td style={pStyles.td}><TopList items={r.top_3_sites} kind="site" /></td>
                <td style={pStyles.td}><TopList items={r.top_3_robots} kind="robot" /></td>
                <td style={pStyles.td}><TopList items={r.top_3_techs} kind="tech" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function TrendCell({ trend, prior }) {
  if (trend === null || trend === undefined)
    return <span style={{color: '#b2bec3', fontSize: 11}}>—</span>;
  if (trend === 'new')
    return <span style={{color: '#c0392b', fontWeight: 600, fontSize: 12}}>NEW</span>;
  const num = typeof trend === 'number' ? trend : parseFloat(trend);
  if (isNaN(num)) return <span>{trend}</span>;
  const arrow = num > 5 ? '↑' : num < -5 ? '↓' : '→';
  const color = num > 5 ? '#c0392b' : num < -5 ? '#0d6e3a' : '#636e72';
  return (
    <span style={{color, fontSize: 12, fontWeight: 600}}>
      {arrow} {num > 0 ? '+' : ''}{num.toFixed(0)}%
      {prior !== undefined && <span style={{color: '#b2bec3', marginLeft: 4, fontWeight: 400}}>(prior {prior})</span>}
    </span>
  );
}

function TopList({ items, kind }) {
  if (!items || items.length === 0)
    return <span style={{color: '#b2bec3'}}>—</span>;
  return (
    <div style={{display: 'flex', flexWrap: 'wrap', gap: 3}}>
      {items.map((x, i) => (
        <span key={`${x}-${i}`} style={pStyles.topChip}>
          {kind === 'site' && x.length > 8 ? x.slice(0, 8) + '…' : x}
        </span>
      ))}
    </div>
  );
}

// ─── Shared layout helpers ─────────────────────────────────────────────

function Section({ title, subtitle, right, children }) {
  return (
    <div style={pStyles.section}>
      <div style={pStyles.sectionHeader}>
        <div>
          <h2 style={pStyles.sectionTitle}>{title}</h2>
          {subtitle && <div style={pStyles.sectionSub}>{subtitle}</div>}
        </div>
        {right}
      </div>
      <div style={pStyles.sectionBody}>{children}</div>
    </div>
  );
}

const Loading = () => <div style={{padding: 24, textAlign: 'center', color: '#636e72'}}>Loading…</div>;
const Empty = ({ msg }) => <div style={{padding: 24, textAlign: 'center', color: '#636e72', fontSize: 13}}>{msg}</div>;
const ErrorBox = ({ message }) => (
  <div style={{padding: 16, background: '#fde2e2', color: '#c0392b', borderRadius: 4}}>
    Error: {message}
  </div>
);

const pStyles = {
  page: { padding: '24px 32px', maxWidth: 1400, fontFamily: "'Inter', -apple-system, sans-serif" },
  title: { fontSize: 26, fontWeight: 700, color: '#2d3436', margin: 0 },
  subtitle: { fontSize: 13, color: '#636e72', margin: '6px 0 24px' },
  section: { background: '#fff', border: '1px solid #e4e8ee', borderRadius: 6,
              marginBottom: 20, overflow: 'hidden' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', borderBottom: '1px solid #e4e8ee', background: '#fafbfc' },
  sectionTitle: { fontSize: 15, margin: 0, fontWeight: 700, color: '#2d3436' },
  sectionSub: { fontSize: 11, color: '#636e72', marginTop: 2 },
  sectionBody: { padding: 16 },
  search: { padding: '6px 10px', borderRadius: 4, border: '1px solid #cbd5e1',
             fontSize: 12, fontFamily: 'inherit', minWidth: 180 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: '#fafbfc',
        borderBottom: '1px solid #e4e8ee', color: '#636e72', fontWeight: 600,
        fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '8px 10px', verticalAlign: 'middle' },
  roleBadge: { background: '#e4e8ee', color: '#475569', padding: '2px 6px',
                borderRadius: 3, fontSize: 11, fontWeight: 600 },
  partChip: { background: '#dbeafe', color: '#1e40af', padding: '2px 8px',
                borderRadius: 3, fontSize: 11, fontWeight: 600 },
  topChip: { background: '#f1f5f9', color: '#475569', padding: '2px 6px',
               borderRadius: 3, fontSize: 11 },
};
